import { DASHBOARD_SCHEDULE_DAYS } from "@/lib/constants";
import { dates } from "@/lib/dates";
import {
  DashboardInterview,
  DashboardScheduleDaySection,
  DashboardScheduleTaskCategory,
  DashboardScheduleWeekDay,
  DashboardScheduleWeekEventLayout,
  DashboardTask,
  TaskType,
} from "@/types";

// Highlight segment layered on top of a day's check-in count. Placement/interview
// check-ins only count toward CHECK_INS; missing-start is separate (not a check-in).
// INTERVIEWS is counted from the day's scheduled interviews, not tasks.
const taskTypeToSegment: Partial<
  Record<TaskType, DashboardScheduleTaskCategory>
> = {
  [TaskType.PLACEMENT_DAY_OF]: DashboardScheduleTaskCategory.STARTS,
  [TaskType.MISSING_START_DATE]: DashboardScheduleTaskCategory.MISSING_START,
};

// Fixed chip order within a day column — the superset first, then segments.
const TASK_CATEGORY_ORDER: DashboardScheduleTaskCategory[] = [
  DashboardScheduleTaskCategory.CHECK_INS,
  DashboardScheduleTaskCategory.STARTS,
  DashboardScheduleTaskCategory.INTERVIEWS,
  DashboardScheduleTaskCategory.MISSING_START,
];

// Types that still owe attention once overdue, so they fold onto today.
// Keep in sync with guac's CALENDAR_CARRYOVER_TASK_TYPES (admin-tasks/types/index.ts).
const CARRYOVER_TASK_TYPES: TaskType[] = [
  TaskType.PLACEMENT_DAY_OF,
  TaskType.PLACEMENT_THIRTY,
  TaskType.PLACEMENT_SIXTY,
  TaskType.PLACEMENT_NINETY,
  TaskType.INTERVIEW_DAY_OF,
  TaskType.MISSING_START_DATE,
];

// Exclusive section a task falls into in the task LIST (one task, one section).
// Distinct from the counts-band model above, where CHECK_INS is the superset.
const taskTypeToListCategory: Record<TaskType, DashboardScheduleTaskCategory> =
  {
    [TaskType.PLACEMENT_DAY_BEFORE]: DashboardScheduleTaskCategory.CHECK_INS,
    [TaskType.PLACEMENT_THIRTY]: DashboardScheduleTaskCategory.CHECK_INS,
    [TaskType.PLACEMENT_SIXTY]: DashboardScheduleTaskCategory.CHECK_INS,
    [TaskType.PLACEMENT_NINETY]: DashboardScheduleTaskCategory.CHECK_INS,
    [TaskType.PLACEMENT_DAY_OF]: DashboardScheduleTaskCategory.STARTS,
    [TaskType.INTERVIEW_DAY_BEFORE]: DashboardScheduleTaskCategory.INTERVIEWS,
    [TaskType.INTERVIEW_DAY_OF]: DashboardScheduleTaskCategory.INTERVIEWS,
    [TaskType.MISSING_START_DATE]: DashboardScheduleTaskCategory.MISSING_START,
  };

// Section order + headings for the task list. General (user-created, untyped)
// tasks lead, then the automated categories.
export const DASHBOARD_TASK_LIST_CATEGORY_ORDER: DashboardScheduleTaskCategory[] =
  [
    DashboardScheduleTaskCategory.GENERAL,
    DashboardScheduleTaskCategory.CHECK_INS,
    DashboardScheduleTaskCategory.STARTS,
    DashboardScheduleTaskCategory.INTERVIEWS,
    DashboardScheduleTaskCategory.MISSING_START,
  ];

export const dashboardScheduleTaskCategoryLabels: Record<
  DashboardScheduleTaskCategory,
  string
> = {
  [DashboardScheduleTaskCategory.GENERAL]: "General",
  [DashboardScheduleTaskCategory.CHECK_INS]: "Check-ins",
  [DashboardScheduleTaskCategory.STARTS]: "Starts",
  [DashboardScheduleTaskCategory.INTERVIEWS]: "Interviews",
  [DashboardScheduleTaskCategory.MISSING_START]: "Missing starts",
};

const HOUR_HEIGHT_PX = 48;
const START_HOUR = 0;
const END_HOUR = 24;
const TIME_GUTTER_WIDTH_PX = 48;
const DAYS_IN_WEEK = 7;
const DEFAULT_EVENT_MINUTES = 60;
const MIN_EVENT_HEIGHT_PX = 8;
const MINUTES_PER_HOUR = 60;

export const dashboardScheduleWeek = {
  HOUR_HEIGHT_PX,
  START_HOUR,
  TIME_GUTTER_WIDTH_PX,

  gridHeightPx: (): number => (END_HOUR - START_HOUR) * HOUR_HEIGHT_PX,

  hourLabels: (): number[] => {
    const labels: number[] = [];
    for (let hour = START_HOUR; hour < END_HOUR; hour += 1) {
      labels.push(hour);
    }
    return labels;
  },

  formatHourLabel: (hour: number): string => {
    const period = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12.toString()} ${period}`;
  },

  // Buckets interviews by local day key so each week column can look up its own.
  groupInterviewsByDayKey: (
    interviews: DashboardInterview[],
  ): Map<string, DashboardInterview[]> => {
    const byDay = new Map<string, DashboardInterview[]>();
    for (const interview of interviews) {
      const key = dates.localDayKey(new Date(interview.dateTime));
      const existing = byDay.get(key);
      if (existing) {
        existing.push(interview);
      } else {
        byDay.set(key, [interview]);
      }
    }
    return byDay;
  },

  // The task list section a task belongs to. Untyped, user-created tasks fall
  // into the General section.
  listTaskCategory: (type: TaskType | null): DashboardScheduleTaskCategory =>
    type ? taskTypeToListCategory[type] : DashboardScheduleTaskCategory.GENERAL,

  // Sections to show for a given day. Missing-start is a today-only flag, so its
  // section is hidden on other days rather than shown empty ("No missing starts").
  listCategoriesForDay: (day: Date): DashboardScheduleTaskCategory[] => {
    const isToday =
      dates.localDayKey(day) ===
      dates.localDayKey(dates.getStartOfLocalDay(new Date()));
    if (isToday) return DASHBOARD_TASK_LIST_CATEGORY_ORDER;
    return DASHBOARD_TASK_LIST_CATEGORY_ORDER.filter(
      (category) => category !== DashboardScheduleTaskCategory.MISSING_START,
    );
  },

  // Tasks shown in the list for a single day. A task always shows on its own
  // due date; incomplete carryover-type backlog ALSO folds onto today once
  // it's overdue. Missing-start flags surface on today only.
  tasksForDay: (tasks: DashboardTask[], day: Date): DashboardTask[] => {
    const startOfToday = dates.getStartOfLocalDay(new Date());
    const isToday = dates.localDayKey(day) === dates.localDayKey(startOfToday);
    const dayKey = dates.localDayKey(day);

    return tasks.filter((task) => {
      if (
        task.type &&
        taskTypeToListCategory[task.type] ===
          DashboardScheduleTaskCategory.MISSING_START
      ) {
        return isToday;
      }
      if (!task.dueDate) return false;
      const due = dates.taskDueDate(task.dueDate);
      // Always show on the task's actual due date, whatever day that is.
      if (dates.localDayKey(due) === dayKey) return true;

      // Past this point the task isn't due on the rendered day — the only
      // remaining case that can still show it is carryover onto today.
      if (!isToday) return false;

      // Overdue, incomplete, carryover-type tasks fold onto today as backlog.
      return (
        due < startOfToday &&
        !task.completedDate &&
        (!task.type || CARRYOVER_TASK_TYPES.includes(task.type))
      );
    });
  },

  // Rolls tasks into per-day counts for the calendar band. The INTERVIEWS chip
  // counts the day's scheduled interviews directly, not interview tasks.
  buildWeekTaskCounts: (
    tasks: DashboardTask[],
    interviews: DashboardInterview[],
    days: DashboardScheduleWeekDay[],
  ): {
    byDay: Map<
      string,
      {
        categories: {
          category: DashboardScheduleTaskCategory;
          count: number;
        }[];
        total: number;
      }
    >;
    total: number;
  } => {
    const startOfToday = dates.getStartOfLocalDay(new Date());
    const todayKey = dates.localDayKey(startOfToday);
    const weekKeys = new Set(days.map((day) => day.key));
    const hasToday = weekKeys.has(todayKey);

    const counts = new Map<
      string,
      Map<DashboardScheduleTaskCategory, number>
    >();
    const bump = (dayKey: string, category: DashboardScheduleTaskCategory) => {
      const forDay =
        counts.get(dayKey) ?? new Map<DashboardScheduleTaskCategory, number>();
      forDay.set(category, (forDay.get(category) ?? 0) + 1);
      counts.set(dayKey, forDay);
    };

    for (const task of tasks) {
      // General user tasks (no type) aren't part of the check-in counts.
      if (!task.type) continue;
      const segment = taskTypeToSegment[task.type];

      // Missing-start is a today-only flag and is NOT a check-in — count it on its
      // own, never toward CHECK_INS.
      if (segment === DashboardScheduleTaskCategory.MISSING_START) {
        if (!hasToday) continue;
        bump(todayKey, segment);
        continue;
      }
      if (!task.dueDate) continue;

      const due = dates.taskDueDate(task.dueDate);
      const dueKey = dates.localDayKey(due);
      if (weekKeys.has(dueKey)) {
        bump(dueKey, DashboardScheduleTaskCategory.CHECK_INS);
        if (segment) bump(dueKey, segment);
        continue;
      }
      // Off-grid: only past-due backlog of a carryover type folds onto today's
      // TASKS (no segment; future is ignored).
      if (!hasToday || due >= startOfToday) continue;
      if (!CARRYOVER_TASK_TYPES.includes(task.type)) continue;
      bump(todayKey, DashboardScheduleTaskCategory.CHECK_INS);
    }

    for (const interview of interviews) {
      const dayKey = dates.localDayKey(new Date(interview.dateTime));
      if (!weekKeys.has(dayKey)) continue;
      bump(dayKey, DashboardScheduleTaskCategory.INTERVIEWS);
    }

    const byDay = new Map<
      string,
      {
        categories: {
          category: DashboardScheduleTaskCategory;
          count: number;
        }[];
        total: number;
      }
    >();
    let total = 0;
    for (const [dayKey, forDay] of counts) {
      const categories = TASK_CATEGORY_ORDER.filter((c) => forDay.has(c)).map(
        (c) => ({ category: c, count: forDay.get(c) ?? 0 }),
      );
      // Total = check-ins + missing-starts. Starts already sit inside CHECK_INS
      // and interviews are calendar events, not tasks, so neither adds.
      const checkIns = forDay.get(DashboardScheduleTaskCategory.CHECK_INS) ?? 0;
      const missingStart =
        forDay.get(DashboardScheduleTaskCategory.MISSING_START) ?? 0;
      const dayTotal = checkIns + missingStart;
      total += dayTotal;
      byDay.set(dayKey, { categories, total: dayTotal });
    }
    return { byDay, total };
  },

  // Positions each interview within a day column.
  // Vertical placement comes from start time + duration
  // Horizontal placement lays overlapping events side by side
  // Events outside the rendered hour range are dropped.
  getEventLayouts: (
    interviews: DashboardInterview[],
  ): DashboardScheduleWeekEventLayout[] => {
    const gridStartMinutes = START_HOUR * MINUTES_PER_HOUR;
    const gridEndMinutes = END_HOUR * MINUTES_PER_HOUR;

    const placed = interviews
      .map((interview) => {
        const start = new Date(interview.dateTime);
        const startMinutes =
          start.getHours() * MINUTES_PER_HOUR + start.getMinutes();
        if (startMinutes < gridStartMinutes || startMinutes >= gridEndMinutes) {
          return null;
        }
        const durationMinutes =
          interview.durationMinutes ?? DEFAULT_EVENT_MINUTES;
        return {
          interview,
          startMinutes,
          endMinutes: startMinutes + durationMinutes,
          topPx:
            ((startMinutes - gridStartMinutes) / MINUTES_PER_HOUR) *
            HOUR_HEIGHT_PX,
          heightPx: Math.max(
            (durationMinutes / MINUTES_PER_HOUR) * HOUR_HEIGHT_PX,
            MIN_EVENT_HEIGHT_PX,
          ),
        };
      })
      .filter((event) => event != null)
      .sort(
        (a, b) =>
          a.startMinutes - b.startMinutes || b.endMinutes - a.endMinutes,
      );

    const layouts: DashboardScheduleWeekEventLayout[] = [];
    // Walk the events, collecting each run of overlapping events into a group;
    // within a group, each event takes the first free column (reusing columns
    // whose prior event has ended), then all share the group's column count.
    let group: { event: (typeof placed)[number]; column: number }[] = [];
    let columnEndMinutes: number[] = [];
    let groupEndMinutes = -1;

    const flushGroup = () => {
      const columnCount = columnEndMinutes.length;
      for (const { event, column } of group) {
        layouts.push({
          interview: event.interview,
          topPx: event.topPx,
          heightPx: event.heightPx,
          leftPct: (column / columnCount) * 100,
          widthPct: (1 / columnCount) * 100,
        });
      }
      group = [];
      columnEndMinutes = [];
      groupEndMinutes = -1;
    };

    for (const event of placed) {
      if (event.startMinutes >= groupEndMinutes) {
        flushGroup();
      }
      let column = columnEndMinutes.findIndex(
        (endMinutes) => endMinutes <= event.startMinutes,
      );
      if (column === -1) {
        column = columnEndMinutes.length;
      }
      columnEndMinutes[column] = event.endMinutes;
      group.push({ event, column });
      groupEndMinutes = Math.max(groupEndMinutes, event.endMinutes);
    }
    flushGroup();

    return layouts;
  },

  // The seven Sun–Sat columns of the week containing `weekStart`.
  weekDays: (weekStart: Date): DashboardScheduleWeekDay[] => {
    const sunday = dates.getSundayWeekStart(weekStart);
    const todayKey = dates.localDayKey(new Date());

    const days: DashboardScheduleWeekDay[] = [];
    for (let i = 0; i < DAYS_IN_WEEK; i += 1) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);
      const key = dates.localDayKey(date);
      days.push({
        key,
        date,
        weekdayLabel: date.toLocaleDateString("en-US", { weekday: "short" }),
        isToday: key === todayKey,
      });
    }
    return days;
  },

  // Buckets interviews into one section per day for the next DASHBOARD_SCHEDULE_DAYS,
  // starting today. Powers the mini agenda list.
  buildDaySections: (
    interviews: DashboardInterview[],
    now: Date = new Date(),
  ): DashboardScheduleDaySection[] => {
    const today = dates.getStartOfLocalDay(now);

    const byDay = new Map<string, DashboardInterview[]>();
    for (const interview of interviews) {
      const key = dates.localDayKey(new Date(interview.dateTime));
      const existing = byDay.get(key);
      if (existing) {
        existing.push(interview);
      } else {
        byDay.set(key, [interview]);
      }
    }

    const sections: DashboardScheduleDaySection[] = [];
    for (let i = 0; i < DASHBOARD_SCHEDULE_DAYS; i += 1) {
      const day = new Date(today);
      day.setDate(today.getDate() + i);
      const key = dates.localDayKey(day);
      const dayInterviews = (byDay.get(key) ?? [])
        .slice()
        .sort((a, b) => a.dateTime.localeCompare(b.dateTime));

      const listLabel =
        i === 0
          ? "Today"
          : day.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            });

      sections.push({ key, listLabel, interviews: dayInterviews });
    }
    return sections;
  },
};
